import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from '../Form';
import uuid from '@afosto/utils/uuid';
import isEqual from 'lodash.isequal';

export const addIdsToValue = value =>
  value.reduce((acc, result) => {
    if (typeof result === 'object') {
      return [...acc, { ...result, _id: result?._id ?? uuid() }];
    }

    return [...acc, result];
  }, []);

export const removeIdsFromValue = value =>
  value.reduce((acc, result) => {
    if (typeof result === 'object') {
      const { _id, ...otherValues } = result;
      return [...acc, otherValues];
    }

    return [...acc, result];
  }, []);

export default ({ name, value, onChange, removeIds = true }) => {
  const formContext = useFormContext();
  const [data, setData] = useState([]);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const formRef = useRef(formContext);
  const valueIsDifferent = !isEqual(value, valueRef.current);

  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
    formRef.current = formContext;
  });

  useEffect(() => {
    const initialValue = valueRef.current;

    if (initialValue && Array.isArray(initialValue)) {
      setData(addIdsToValue(initialValue));
    }
  }, []);

  useEffect(() => {
    if (valueIsDifferent) {
      setData(valueRef.current);
    }
  }, [valueIsDifferent]);

  const handleDataChange = useCallback(
    (ev, newValue) => {
      const handleChange = onChangeRef.current;
      const formattedValue = removeIds ? removeIdsFromValue(newValue) : newValue;

      if (formRef.current && !handleChange) {
        const { setValue } = formRef.current ?? {};

        if (setValue) {
          setValue(name, formattedValue);
        }

        return;
      }

      if (handleChange) {
        handleChange(ev, formattedValue);
      }
    },
    [name, removeIds],
  );

  const append = useCallback(
    newValue => {
      setData(oldData => {
        const formattedValue =
          typeof newValue === 'object' ? { ...newValue, _id: uuid() } : newValue;
        const updatedData = [...oldData, formattedValue];
        handleDataChange(null, updatedData);

        return updatedData;
      });
    },
    [handleDataChange],
  );

  const remove = useCallback(
    index => {
      setData(oldData => {
        const updatedData = [...oldData.slice(0, index), ...oldData.slice(index + 1)];
        handleDataChange(null, updatedData);

        return updatedData;
      });
    },
    [handleDataChange],
  );

  const update = useCallback(
    (index, field) => (ev, newValue) => {
      setData(oldData => {
        const updatedData = [
          ...oldData.slice(0, index),
          { ...oldData[index], [field]: newValue },
          ...oldData.slice(index + 1),
        ];
        handleDataChange(ev, updatedData);

        return updatedData;
      });
    },
    [handleDataChange],
  );

  return {
    data,
    setData,
    append,
    remove,
    update,
  };
};
